import { useToast } from "@dash/toast";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient, type QueryKey } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";
import { Uuid } from "@megaron/uuid";

import { buildContactOptions, ContactOption } from "./customerSupportTicketsContactOptions";

type Params = {
  customerUuid: Uuid;
  defaultReporterEmail: string;
  defaultReporterName: string;
  ticketsListQueryKey: QueryKey;
};

export function useCustomerSupportCreateTicketForm(params: Params) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reporterEmail, setReporterEmail] = useState(params.defaultReporterEmail);
  const [reporterName, setReporterName] = useState(params.defaultReporterName);
  const [selectedContact, setSelectedContact] = useState("");
  const [sourceMailbox, setSourceMailbox] = useState("");

  const mailboxesQuery = useClientManager("support").imapConnectionStatus().useQuery({}, { enabled: isDialogOpen });
  const customerQuery = useClientManager("crm").customerDocBulkQuery().useQuery(
    { customerUuids: [params.customerUuid] },
    { enabled: isDialogOpen },
  );
  const contactsQuery = useClientManager("crm").customerContactsQuery().useQuery(
    { customerId: params.customerUuid },
    { enabled: isDialogOpen },
  );

  useEffect(() => {
    if (!isDialogOpen) {
      setReporterEmail(params.defaultReporterEmail);
      setReporterName(params.defaultReporterName);
      setSubject("");
      setMessage("");
      setSelectedContact("");
      setSourceMailbox("");
    }
  }, [params.defaultReporterEmail, params.defaultReporterName, isDialogOpen]);

  const mailboxOptions = useMemo(
    () => (mailboxesQuery.data?.mailboxes ?? []).map((mailbox) => ({ label: mailbox.email, value: mailbox.email })),
    [mailboxesQuery.data?.mailboxes],
  );

  const contactOptions = useMemo((): ContactOption[] => {
    return buildContactOptions({
      customer: customerQuery.data?.[0],
      contacts: contactsQuery.data,
      defaultReporterEmail: params.defaultReporterEmail,
      defaultReporterName: params.defaultReporterName,
    });
  }, [contactsQuery.data, customerQuery.data, params.defaultReporterEmail, params.defaultReporterName]);

  useEffect(() => {
    if (!isDialogOpen || contactOptions.length === 0) return;
    const current = contactOptions.find(
      (option) => option.email === reporterEmail.trim().toLowerCase() && option.name === reporterName.trim(),
    );
    if (current) {
      setSelectedContact(current.value);
      return;
    }
    const byEmail = contactOptions.find((option) => option.email === reporterEmail.trim().toLowerCase());
    if (byEmail) {
      setSelectedContact(byEmail.value);
      setReporterName(byEmail.name);
      return;
    }
    const first = contactOptions[0];
    setSelectedContact(first.value);
    setReporterEmail(first.email);
    setReporterName(first.name);
  }, [contactOptions, isDialogOpen, reporterEmail, reporterName]);

  const createTicketMutation = useClientManager("support")
    .createTicket()
    .useMutation({
      onSuccess: () => {
        queryClient.invalidateQueries(params.ticketsListQueryKey);
        toast.info("Utworzono zgłoszenie");
        setIsDialogOpen(false);
      },
      onError: () => {
        toast.error("Nie udało się utworzyć zgłoszenia");
      },
    });

  const handleCreateTicket = () => {
    const trimmedSubject = subject.trim();
    const trimmedReporterEmail = reporterEmail.trim();
    const trimmedReporterName = reporterName.trim();
    const trimmedSourceMailbox = sourceMailbox.trim();
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedReporterEmail || !trimmedMessage) {
      toast.error("Uzupełnij temat, wiadomość i kontakt");
      return;
    }
    const reporterEmailResult = Serializers.email.deserialize(trimmedReporterEmail);
    if (reporterEmailResult.isFailure) {
      toast.error("Podaj poprawny email kontaktu");
      return;
    }
    createTicketMutation.mutate({
      subject: trimmedSubject,
      message: trimmedMessage,
      reporterEmail: reporterEmailResult.value,
      reporterName: trimmedReporterName || trimmedReporterEmail.split("@")[0],
      sourceMailbox: trimmedSourceMailbox || null,
      status: "open",
      priority: null,
      tags: null,
      assigneeEmails: null,
      attachments: null,
    });
  };

  const isBusy = "isLoading" in createTicketMutation ? createTicketMutation.isLoading : false;

  const closeDialog = () => {
    if (!isBusy) setIsDialogOpen(false);
  };

  const onContactSelectChange = (value: string | undefined) => {
    const selected = contactOptions.find((option) => option.value === value);
    if (!selected) return;
    setSelectedContact(selected.value);
    setReporterEmail(selected.email);
    setReporterName(selected.name);
  };

  return {
    isDialogOpen,
    setIsDialogOpen,
    closeDialog,
    subject,
    setSubject,
    message,
    setMessage,
    selectedContact,
    sourceMailbox,
    setSourceMailbox,
    mailboxesQuery,
    contactOptions,
    mailboxOptions,
    onContactSelectChange,
    handleCreateTicket,
    isBusy,
  };
}
